import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Card, Icon, type IconName } from '@kollez/design-system';
import { useMyTimeline, type MyTimelineEvent, type MyTimelineKind } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /activity — student's own reverse-chronological event feed.
 *
 * Consumes GET /students/me/timeline (Pass 2 relationship endpoint) which merges
 * Application transitions, Interview scheduling + feedback, Offer released/
 * accepted/declined, Badge earned/revoked, AssessmentAttempt start/submit,
 * and High/Urgent Notifications into one stream. This is the honest 'my
 * placement story' surface — every prior version cobbled together disparate
 * per-collection lists.
 */

const KIND_LABEL: Record<MyTimelineKind, string> = {
  application: 'Applications',
  interview: 'Interviews',
  offer: 'Offers',
  badge: 'Badges',
  attempt: 'Assessments',
  notification: 'Alerts',
};

const KIND_ICON: Record<MyTimelineKind, IconName> = {
  application: 'layers',
  interview: 'user-check',
  offer: 'file-signature',
  badge: 'award',
  attempt: 'clipboard-check',
  notification: 'bell',
};

const KIND_TONE: Record<MyTimelineKind, string> = {
  application: 'badge-info',
  interview: 'badge-info',
  offer: 'badge-success',
  badge: 'badge-success',
  attempt: '',
  notification: 'badge-warning',
};

function linkFor(ev: MyTimelineEvent): string | null {
  switch (ev.refKind) {
    case 'Application': return `/applications/${ev.refId}`;
    case 'Interview': return `/interviews/${ev.refId}`;
    case 'Offer': return `/offers/${ev.refId}`;
    case 'Badge': return `/profile/badges/${ev.refId}`;
    case 'AssessmentAttempt': return `/attempts/${ev.refId}`;
    case 'Notification': return `/notifications`;
    default: return null;
  }
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const midNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.floor((midNow.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return d.toLocaleString('en-IN', { weekday: 'long' });
  return formatIndianDate(d);
}

const KINDS: MyTimelineKind[] = ['application', 'interview', 'offer', 'badge', 'attempt', 'notification'];

export function StudentActivityPage() {
  const [kind, setKind] = useState<MyTimelineKind | ''>('');
  const q = useMyTimeline({ limit: 200, sortDir: 'desc', ...(kind ? { kind } : {}) });
  const events = q.data?.events ?? [];

  const grouped = useMemo(() => {
    const g: Record<string, MyTimelineEvent[]> = {};
    for (const ev of events) {
      const bucket = dayLabel(ev.at);
      (g[bucket] ??= []).push(ev);
    }
    return g;
  }, [events]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Progress</p>
          <h1 className="h-page">My activity</h1>
          <p className="subhead">
            Every application move, interview, offer, badge and important alert — merged into one
            feed so you can see how your placement season is unfolding.
          </p>
        </div>
      </div>

      <Card padding="md">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`tag ${!kind ? 'tag-active' : ''}`}
            aria-pressed={!kind}
            onClick={() => setKind('')}
          >
            All
          </button>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`tag ${kind === k ? 'tag-active' : ''}`}
              aria-pressed={kind === k}
              onClick={() => setKind(kind === k ? '' : k)}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </Card>

      <Card padding="none">
        <AsyncBoundary
          kind="detail"
          inCard
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && events.length === 0}
          emptyTitle={kind ? `No ${KIND_LABEL[kind].toLowerCase()} yet` : 'No activity yet'}
          emptyHint="Your first application, interview, or offer will show up here."
          onRetry={() => void q.refetch()}
        >
          <div>
            {Object.entries(grouped).map(([day, items]) => (
              <section key={day}>
                <div className="border-t border-line-tertiary px-5 py-2 text-xs font-semibold text-ink-tertiary" style={{ background: 'rgb(var(--bg-secondary))' }}>
                  {day}
                </div>
                <ol className="m-0 p-0 list-none">
                  {items.map((ev, i) => {
                    const href = linkFor(ev);
                    const rowClass = `flex items-start gap-3 px-5 py-3 border-t border-line-tertiary ${href ? 'hover:bg-secondary' : ''}`;
                    const inner = (
                      <>
                        <span className={`badge ${KIND_TONE[ev.kind]}`}>
                          <Icon name={KIND_ICON[ev.kind]} size={12} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="cell-title">{ev.title}</div>
                          {ev.detail && <div className="cell-sub truncate">{ev.detail}</div>}
                        </div>
                        <div className="cell-sub text-right whitespace-nowrap">
                          {formatTime(ev.at)}
                        </div>
                      </>
                    );
                    return (
                      <li key={`${ev.kind}-${ev.refId}-${i}`}>
                        {href
                          ? <Link to={href} className={rowClass}>{inner}</Link>
                          : <div className={rowClass}>{inner}</div>}
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
          </div>
        </AsyncBoundary>
      </Card>
    </div>
  );
}
